'use client';

import type { PointerEvent } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Numeric } from '@/components/ui/numeric';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { cn } from '@/lib/utils';
import type { DrawMatch } from '@/draw/types';
import { DrawPlayerLink } from '@/app/[locale]/event/draw/components/DrawPlayerLink';
import { drawPlayerLabel } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';
import { useDrawHighlight } from './DrawHighlight';
import { hasOneBye } from '../libs/ladderModel';
import { matchGames } from '../libs/poolGrid';

const formatMatchTime = (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));

const DEFAULT_GAME_SLOTS = 3;

export interface MatchCardProps {
  match: DrawMatch;
  code?: string;
  next?: string;
  /** "Loser → Plate R16-1": where the loser plays next; jumps there when the draw view provides paths. */
  loser?: { label: string; matchId: string };
  placeholders?: [string | null, string | null];
  playerHref?: (id: string) => string | undefined;
  /** Game cells per row; the round's highest format so every card in a column matches. */
  gameSlots?: number;
  className?: string;
}

export function MatchCard({
  match,
  code,
  next,
  loser,
  placeholders,
  playerHref,
  gameSlots = DEFAULT_GAME_SLOTS,
  className,
}: MatchCardProps) {
  const t = useTranslations('Event.drawView.ladder');
  const tMatches = useTranslations('Matches');
  const tPreview = useTranslations('AdminDraw.drawForm.drawPreview');
  const formatName = useFormatName();
  const outcomeLabel = useOutcomeLabel();
  const locale = useLocale();
  const highlight = useDrawHighlight();

  const live = match.status === 'in_progress';
  const done = match.status === 'completed';
  const winnerless = isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover });
  // A walkover still has a winner; dual withdrawal / not played do not.
  const winnerId =
    !winnerless || match.outcome === 'walkover' || (match.outcome == null && match.isWalkover)
      ? match.winner?.id
      : undefined;
  const games = match.score ?? [];
  const slots = Math.max(gameSlots, games.length);
  const gamesWon = matchGames(match);
  const showGames = (done || live) && !winnerless;
  const schedule = match.eventMatchSchedule;
  const retired = done && match.outcome === 'retired';
  const hasTime = !live && !winnerless && !!schedule?.scheduled_datetime;
  const hasCourt = !!schedule?.court_name;
  const showFooter =
    !!code || live || winnerless || retired || hasTime || hasCourt || !!next || !!loser;

  // Path tracing (mouse only; touch has no hover and keeps the loser link).
  const teamIds = [match.player1, match.player2].flatMap((p) => (p && !p.isBye ? [p.id] : []));
  const onTeamPath = !!highlight?.team && teamIds.includes(highlight.team);
  const path =
    highlight?.winnerTarget === match.matchId
      ? 'winner'
      : highlight?.loserTarget === match.matchId
        ? 'loser'
        : undefined;
  const faded = !!highlight?.team && !onTeamPath && !path;
  const onMouse = (fn: () => void) => (e: PointerEvent) => {
    if (e.pointerType === 'mouse') fn();
  };

  const nameOf = (key: 'player1' | 'player2', i: 0 | 1) => {
    const player = match[key];
    return player?.isBye
      ? tPreview('bye')
      : player
        ? drawPlayerLabel(player, formatName)
        : (placeholders?.[i] ?? tPreview('tbd'));
  };

  const teamName = (key: 'player1' | 'player2', i: 0 | 1, className?: string) => {
    const player = match[key];
    const label = nameOf(key, i);
    return (
      <span
        className={cn(
          "min-w-0 flex-1 font-semibold [&_a]:after:absolute [&_a]:after:inset-0 [&_a]:after:content-['']",
          NAME_WRAP,
          className,
        )}
      >
        {player && !player.isBye ? (
          <DrawPlayerLink playerId={player.id} playerHref={playerHref}>
            <NameText name={label} />
          </DrawPlayerLink>
        ) : (
          label
        )}
      </span>
    );
  };

  const hoverProps = (player: DrawMatch['player1']) => {
    const real = !!player && !player.isBye;
    return {
      onPointerEnter:
        highlight && real ? onMouse(() => highlight.hoverTeam(player.id)) : undefined,
      onPointerLeave: highlight && real ? onMouse(() => highlight.hoverTeam(null)) : undefined,
    };
  };

  const row = (key: 'player1' | 'player2', i: 0 | 1) => {
    const player = match[key];
    const other = match[key === 'player1' ? 'player2' : 'player1'];
    // The lone team facing a BYE goes through even when no winner is stored (plate byes).
    const won =
      (done && winnerId != null && player?.id === winnerId) ||
      (!!player && !player.isBye && !!other?.isBye);
    const lost = done && winnerId != null && !won;
    const real = !!player && !player.isBye;
    const onRow = real && highlight?.team === player.id;
    const mine = i === 0 ? gamesWon.player1 : gamesWon.player2;
    return (
      <div
        data-team-row={onRow || undefined}
        {...hoverProps(player)}
        className={cn(
          'relative flex min-h-11 items-center gap-2 px-3 py-1 text-sm md:min-h-8',
          i === 1 && 'border-border border-t',
          // The winner's row carries the amber edge the bracket lines continue.
          won && 'before:bg-bracket-path before:absolute before:inset-y-0 before:left-0 before:w-[3px]',
          onRow && 'bg-secondary-emphasis/10',
        )}
      >
        {teamName(
          key,
          i,
          cn(
            won && 'font-bold',
            lost && 'text-muted font-normal',
            (!player || player.isBye) && 'text-muted font-normal italic',
          ),
        )}
        <Numeric
          className={cn(
            'size-5 shrink-0 rounded-[5px] text-center text-[0.71875rem] leading-5 font-bold',
            showGames && won ? 'bg-primary text-primary-foreground' : 'bg-surface text-muted',
          )}
        >
          {showGames ? mine : '–'}
        </Numeric>
        <span className="flex shrink-0">
          {Array.from({ length: slots }, (_, k) => {
            const g = showGames ? games[k] : undefined;
            const myScore = g ? (i === 0 ? g.player1Score : g.player2Score) : null;
            const theirScore = g ? (i === 0 ? g.player2Score : g.player1Score) : null;
            const wonGame = won && myScore !== null && theirScore !== null && myScore > theirScore;
            return (
              <Numeric
                key={k}
                className={cn(
                  'w-5 text-center text-xs leading-5',
                  myScore === null && 'text-border-strong',
                  myScore !== null && !wonGame && 'text-muted',
                  wonGame && 'text-foreground font-bold',
                )}
              >
                {myScore ?? '–'}
              </Numeric>
            );
          })}
        </span>
      </div>
    );
  };

  // A match with one BYE seat is one line: the team that goes through, then the BYE tag.
  const oneBye = hasOneBye(match);
  const byeLine = () => {
    const key = match.player1?.isBye ? 'player2' : 'player1';
    const i = key === 'player1' ? 0 : 1;
    const player = match[key];
    const real = !!player && !player.isBye;
    const onRow = real && highlight?.team === player.id;
    return (
      <div
        data-team-row={onRow || undefined}
        {...hoverProps(player)}
        className={cn(
          'relative flex min-h-11 items-center gap-2 px-3 py-1 text-sm md:min-h-8',
          real &&
            'before:bg-bracket-path before:absolute before:inset-y-0 before:left-0 before:w-[3px]',
          onRow && 'bg-secondary-emphasis/10',
        )}
      >
        {teamName(key, i, cn(!real && 'text-muted font-normal italic'))}
        <span className="text-muted border-border shrink-0 rounded border px-1.5 font-mono text-[0.625rem] leading-4 font-semibold tracking-wider uppercase">
          {t('byeTag')}
        </span>
      </div>
    );
  };

  // Nobody plays a BYE v BYE match: one line with just the tag.
  const bothByes = !!match.player1?.isBye && !!match.player2?.isBye;
  const doubleByeLine = () => (
    <div className="relative flex min-h-11 items-center justify-end gap-2 px-3 py-1 text-sm md:min-h-8">
      <span className="text-muted border-border shrink-0 rounded border px-1.5 font-mono text-[0.625rem] leading-4 font-semibold tracking-wider uppercase">
        {t('byeTag')}
      </span>
    </div>
  );

  return (
    <div
      data-match-card
      data-match-id={match.matchId}
      data-path={path}
      data-team-path={onTeamPath || undefined}
      data-faded={faded || undefined}
      // A jump from a "Loser → …" link lands keyboard focus here.
      tabIndex={highlight ? -1 : undefined}
      onPointerEnter={highlight ? onMouse(() => highlight.hoverMatch(match.matchId)) : undefined}
      onPointerLeave={highlight ? onMouse(() => highlight.hoverMatch(null)) : undefined}
      className={cn(
        'bg-card border-border focus-visible:ring-bracket-path w-full min-w-0 overflow-hidden rounded-lg border transition-opacity duration-150 focus:outline-none focus-visible:ring-2',
        live && 'border-danger ring-danger ring-1',
        onTeamPath && 'ring-secondary-emphasis ring-2',
        path === 'winner' && 'ring-success ring-2',
        path === 'loser' && 'ring-bracket-path ring-2',
        faded && 'opacity-40',
        className,
      )}
    >
      {bothByes ? (
        doubleByeLine()
      ) : oneBye ? (
        byeLine()
      ) : (
        <>
          {row('player1', 0)}
          {row('player2', 1)}
          {showFooter && (
            <div className="bg-surface border-border text-muted flex min-h-5.5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 border-t px-3 py-0.5 text-[0.6875rem]">
              {code && <span className="text-foreground shrink-0 font-mono font-semibold">{code}</span>}
              {code && (live || winnerless || retired || hasTime) && <span aria-hidden>·</span>}
              {live ? (
                <span className="text-danger font-bold uppercase">{t('live')}</span>
              ) : winnerless ? (
                <span>{outcomeLabel(match.outcome, match.isWalkover === true)}</span>
              ) : hasTime ? (
                <LocalDateTime
                  iso={schedule!.scheduled_datetime!}
                  locale={locale}
                  format={formatMatchTime}
                />
              ) : null}
              {retired && (
                <span>
                  {hasTime ? '· ' : ''}
                  {tMatches('retiredShort')}
                </span>
              )}
              {loser ? (
                <button
                  type="button"
                  data-loser-link
                  title={loser.label}
                  onClick={() => highlight?.jump(loser.matchId)}
                  className="text-warning-emphasis focus-visible:ring-ring relative ml-auto min-w-0 text-right font-semibold break-words hover:underline focus-visible:ring-2 focus-visible:outline-none pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:bottom-0 pointer-coarse:after:h-11 pointer-coarse:after:content-['']"
                >
                  {loser.label}
                </button>
              ) : (
                hasCourt && (
                  <span className="ml-auto min-w-0 break-words">{schedule?.court_name}</span>
                )
              )}
              {next && (
                <span
                  data-next-link
                  className={cn(
                    'text-foreground shrink-0 font-semibold',
                    !hasCourt && !loser && 'ml-auto',
                  )}
                >
                  {t('next', { code: next })}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
